import Controller from '@ember/controller'
import { service } from '@ember/service'
import { action } from '@ember/object'
import { didCancel } from 'ember-concurrency'
import { isAbortError } from 'teacher-ui-client/errors/http-error'
import type RouterService from '@ember/routing/router-service'
import type StudentImportService from 'teacher-ui-client/services/student-import'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import type { IntlService } from 'ember-intl'
import type { Log } from '@blakeelearning/log'
import type ConfirmRoute from 'teacher-ui-client/manage/import/students/confirm/route'
import type { ImportCsvResponse } from 'teacher-ui-client/services/student-import'
import type PollingStatus from 'teacher-ui-client/student-import/polling-status'

export default class ImportStudentsConfirmController extends Controller<ModelFor<ConfirmRoute>> {
  @service router!: RouterService

  @service studentImport!: StudentImportService

  @service flashQueue!: FlashQueueService

  @service intl!: IntlService

  @service log!: Log

  get subscriptionType() {
    return this.model.subscriptionType
  }

  get isSubco() {
    return this.model.isSubco
  }

  get importJob() {
    return this.studentImport.importJob
  }

  get candidateStudents() {
    return this.importJob.candidateStudents
  }

  get duplicateStudentsInSource() {
    return this.candidateStudents?.some((student) => student.duplicateSource)
  }

  get duplicateStudentsInDestination() {
    return this.candidateStudents?.some((student) => student.duplicateDestination)
  }

  get studentsNameChanged() {
    return this.candidateStudents?.some((student) => student.studentNameChange)
  }

  get displayWarning() {
    return this.duplicateStudentsInSource || this.duplicateStudentsInDestination || this.studentsNameChanged
  }

  get filename() {
    return this.importJob.file?.name
  }

  get studentsCount() {
    return this.candidateStudents?.length
  }

  get isImporting() {
    return this.studentImport.importCsv.isRunning
  }

  /**
   * Start an async process of uploading/importing students
   */
  async confirmImport() {
    try {
      const importResult = await this.studentImport.importCsv.perform()

      const { importJob } = this
      if (importJob.imported) {
        this.flashQueue.clearMessages()
        if (this._shouldRedirectToManageStudents) {
          const message = this.intl.t('manage.studentCsv.importSuccessful')
          this.flashQueue.addSuccess({ subtitle: message })
          void this.router.replaceWith('manage.students')
          return
        } else {
          const manageStudentsUrl = this.router.urlFor('manage.students')
          const message = this.intl.t('manage.studentCsv.importSuccessfulOtherPage', {
            htmlSafe: true,
            manageStudentsUrl,
          })
          this.flashQueue.addSuccess({ subtitle: message })
        }
      } else {
        this.unexpectedResultHandler(importResult)
      }
    } catch (error) {
      // should only reject for task cancel or network failure
      if (!didCancel(error) && !isAbortError(error)) {
        this.unexpectedErrorHandler(error)
      }
    }
  }

  // only replace the route with manage students if we're on the confirm import route
  get _shouldRedirectToManageStudents() {
    return this._isConfirmImportRoute
  }

  get _isConfirmImportRoute() {
    return this.router.currentRouteName === 'manage.import.students.confirm'
  }

  // Attempts to manage unexpected responses
  unexpectedResultHandler({
    response,
    responseBody,
  }: {
    response: {
      ok: boolean
    }
    responseBody: { pollingStatus?: PollingStatus } | ImportCsvResponse
  }) {
    if (!response?.ok && 'pollingStatus' in responseBody) {
      // Failed polling. Shouldn't happen, but we can be prepared for it
      const pollingStatus = responseBody.pollingStatus
      const pollingJobId = pollingStatus?.jobId

      this.flashQueue.clearMessages()
      this.flashQueue.addFail({ subtitle: this.intl.t('manage.studentCsv.importPollingError') })

      this.log.error(`Polling of student import status failed. jobId: ${pollingJobId}`, { pollingJobId })
    } else {
      // Some unexpected response error
      const error = 'errors' in responseBody ? responseBody?.errors?.[0] : { response, responseBody }
      this.unexpectedErrorHandler(error)
    }
  }

  // Stay on the confirm page and display an error flash
  unexpectedErrorHandler(error: any) {
    this.flashQueue.clearMessages()
    this.flashQueue.addFail({ subtitle: this.intl.t('manage.studentCsv.importError') })

    this.log.error('Unable to upload student import', { uploadResponse: error })
  }

  @action
  cancel() {
    this.studentImport.cancelImportCsv()

    this.flashQueue.clearMessages()
    void this.router.replaceWith('manage.students')
  }

  @action
  submit() {
    void this.confirmImport()
  }
}
